import React, { Fragment } from "react";
import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { CheckCircle, PauseCircle, X } from "@phosphor-icons/react";
import { Box, FormControl, List, ListItem, MenuItem, Select } from "@mui/material";
import apiSauce from "../../config/apisauce";
import { useFormik } from "formik";
import { mixed, number, object, string } from "yup";
import useNotiStack from "../../hooks/useNotiStack";
import { SUSPENSION_REASONS_LIST } from "../../enums/suspensionEnums";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { formatSlugToTitle } from "../../helpers/stringHelper";
import { route } from "../../../../vendor/tightenco/ziggy/src/js";
import { formatIsoDate } from "../../helpers/dateHelper";
import CustomDialog from "./CustomDialog";

export default function SuspendCreatorDialog({ creator, mode, setMode, open, setOpen, onSuccess }) {
  const { error } = useNotiStack();

  const { mutate, isPending } = useMutation({
    mutationFn: async (payload) => {
      try {
        const { data } = await axios.post("/suspensions", payload);
        return data;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    onSuccess: () => {
      setMode("suspension-success");
      onSuccess();
    },
    onError: ({ response }) => {
      error(response.data.message);
    },
  });

  const formik = useFormik({
    initialValues: {
      duration: null,
      requestLimit: null,
      reason: null,
    },
    validationSchema: object({
      duration: mixed().required("Suspension duration is required"),
      requestLimit: number()
        .typeError("Daily request limit must be a number")
        .integer("Daily request limit must be an integer")
        .min(0, "Must be at least 0 request per day")
        .required("Daily request limit is required"),
      reason: string().trim().required("Reason is required"),
    }),
    onSubmit: () => {
      setMode("confirm-suspension-action");
    },
  });

  const handleSubmitDismissReport = async () => {
    mutate({
      userId: creator.id,
      duration: formik.values.duration,
      requestLimit: formik.values.requestLimit,
      reason: formik.values.reason,
    });
  };

  const handleLiftSuspension = async () => {
    try {
      await apiSauce.delete(route("liftSuspension", creator.id));
      setMode("suspension-lifted-successfully");
      onSuccess();
    } catch (error) {
      error("Oops! Something went wrong. Please refresh the page or try again.");
    }
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const ModeComponentMap = {
    "suspension-form": {
      title: "Suspend Creator",
      component: <SuspendCreatorForm formik={formik} setMode={setMode} onClose={handleClose} />,
    },
    "confirm-suspension-action": {
      title: "Confirm Suspension",
      component: (
        <ConfirmSuspensionAction
          handleSubmitDismissReport={handleSubmitDismissReport}
          onClose={handleClose}
          isLoading={isPending}
        />
      ),
    },
    "suspension-success": {
      title: "Creator Has Been Suspended",
      component: <SuccessfullySuspendedPanel onClose={handleClose} />,
    },
    "lift-suspension-action": {
      title: "Lift Creator Suspension",
      component: (
        <ConfirmEndSuspensionAction handleLiftSuspension={handleLiftSuspension} creator={creator} onClose={handleClose} />
      ),
    },
    "suspension-lifted-successfully": {
      title: "Account Suspension Lifted",
      component: <SuspensionLiftedSuccessfully onClose={handleClose} />,
    },
  };

  return (
    <CustomDialog onClose={handleClose} open={open}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <DialogTitle sx={{ m: 0, p: 0 }}>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 700,
              fontSize: "24px",
              lineHeight: "100%",
              textAlign: "center",
              color: "primary.main",
            }}
          >
            {ModeComponentMap[mode]?.title || "Suspend Creator Dialog"}
          </Typography>
        </DialogTitle>
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <X />
        </IconButton>
      </Box>

      {ModeComponentMap[mode]?.component ?? <Box p={3}>Invalid mode</Box>}
    </CustomDialog>
  );
}

const suspensionDurations = [
  { label: "1 day", value: 1 },
  { label: "2 days", value: 2 },
  { label: "3 days", value: 3 },
  { label: "5 days", value: 5 },
  { label: "7 days", value: 7 },
  { label: "10 days", value: 10 },
  { label: "14 days", value: 14 },
  { label: "Indefinite", value: "indefinite" },
];

const requestLimit = [
  { label: "0 request/day", value: 0 },
  { label: "1 request/day", value: 1 },
  { label: "2 requests/day", value: 2 },
  { label: "3 requests/day", value: 3 },
  { label: "5 requests/day", value: 5 },
];

const SuspendCreatorForm = ({ formik, onClose }) => {
  return (
    <Fragment>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 120 }}>
        <FormControl fullWidth>
          <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>Suspension Duration</Typography>
          <Select
            displayEmpty
            sx={{ borderRadius: "10px" }}
            value={formik.values.duration}
            onChange={(event) => formik.setFieldValue("duration", event.target.value)}
            error={formik.touched.duration && Boolean(formik.errors.duration)}
          >
            <MenuItem value={null} disabled>
              Select an option
            </MenuItem>
            {suspensionDurations.map((duration) => (
              <MenuItem key={`suspension-duration-${duration.value}`} value={duration.value}>
                {duration.label}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.duration && formik.errors.duration && (
            <Typography color="error" variant="caption" sx={{ mt: 0.5, mx: "14px" }}>
              {formik.errors.duration}
            </Typography>
          )}
        </FormControl>

        <FormControl fullWidth>
          <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>Daily Request Limit</Typography>
          <Select
            displayEmpty
            sx={{ borderRadius: "10px" }}
            value={formik.values.requestLimit}
            onChange={(event) => formik.setFieldValue("requestLimit", event.target.value)}
            error={formik.touched.requestLimit && Boolean(formik.errors.requestLimit)}
          >
            <MenuItem value={null} disabled>
              Select an option
            </MenuItem>
            {requestLimit.map((requestLimit) => (
              <MenuItem key={`suspension-request-limit-${requestLimit.value}`} value={requestLimit.value}>
                {requestLimit.label}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.requestLimit && formik.errors.requestLimit && (
            <Typography color="error" variant="caption" sx={{ mt: 0.5, mx: "14px" }}>
              {formik.errors.requestLimit}
            </Typography>
          )}
        </FormControl>

        <FormControl fullWidth>
          <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>Reason</Typography>
          <Select
            displayEmpty
            sx={{ borderRadius: "10px" }}
            value={formik.values.reason}
            onChange={(event) => formik.setFieldValue("reason", event.target.value)}
            error={formik.touched.reason && Boolean(formik.errors.reason)}
          >
            <MenuItem value={null} disabled>
              Select an option
            </MenuItem>
            {SUSPENSION_REASONS_LIST.map((reason) => (
              <MenuItem key={`suspension-reason-${reason.value}`} value={reason.value}>
                {reason.label}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.reason && formik.errors.reason && (
            <Typography color="error" variant="caption" sx={{ mt: 0.5, mx: "14px" }}>
              {formik.errors.reason}
            </Typography>
          )}
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ padding: "0 !important", pt: "16px !important" }}>
        <Button sx={{ minWidth: 150 }} onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button sx={{ minWidth: 150 }} variant="contained" onClick={formik.handleSubmit}>
          Apply Suspension
        </Button>
      </DialogActions>
    </Fragment>
  );
};

const ConfirmSuspensionAction = ({ handleSubmitDismissReport, onClose, isLoading }) => {
  return (
    <Fragment>
      <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            bgcolor: "#DEF2FC",
            color: "primary.main",
            borderRadius: 50,
            height: 150,
            width: 150,
          }}
        >
          <PauseCircle size={150} weight="thin" />
        </Box>
        <Typography textAlign={"center"} px={10} mb={1}>
          You are about to place this profile under suspension. Please confirm to proceed with this action.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ padding: "0 !important", pt: "16px !important" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 2 }}>
          <Button fullWidth onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            loadingPosition="start"
            variant="contained"
            color="warning"
            size="large"
            onClick={handleSubmitDismissReport}
          >
            Apply Suspension
          </Button>
        </Box>
      </DialogActions>
    </Fragment>
  );
};

const SuccessfullySuspendedPanel = ({ onClose }) => {
  return (
    <Fragment>
      <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            bgcolor: "#DEF2FC",
            color: "primary.main",
            borderRadius: 50,
            height: 150,
            width: 150,
          }}
        >
          <CheckCircle size={150} weight="thin" />
        </Box>
        <Typography textAlign={"center"} px={5} mb={1}>
          The creator will be notified and an email has been sent regarding the suspension details.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ padding: "0 !important", pt: "16px !important" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 2, justifyContent: "center" }}>
          <Button sx={{ width: " 50%" }} onClick={onClose} variant="contained">
            Close
          </Button>
        </Box>
      </DialogActions>
    </Fragment>
  );
};

const ConfirmEndSuspensionAction = ({ creator, handleLiftSuspension, onClose, isLoading = false }) => {
  return (
    <Fragment>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1, pt: "0 !important" }}>
        <Typography mb={1}>
          You are about to lift the suspension and remove the daily collaboration request limit from this creator’s account.
        </Typography>

        <Box>
          <List sx={{ listStyleType: "disc", pl: 3, py: 0 }}>
            <ListItem sx={{ display: "list-item", pl: 0, py: 0 }}>
              Creator Profile:&nbsp;
              <Typography component="span" fontWeight={700}>
                {creator.name}
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item", pl: 0, py: 0 }}>
              Date of Suspension:&nbsp;
              <Typography component="span" fontWeight={700}>
                {formatIsoDate(creator.latest_suspension.created_at)}
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item", pl: 0, py: 0 }}>
              Reason:&nbsp;
              <Typography component={"span"} fontWeight={700}>
                {formatSlugToTitle(creator.latest_suspension.reason)}
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item", pl: 0, py: 0 }}>
              Duration:&nbsp;
              <Typography component={"span"} fontWeight={700}>
                {creator.latest_suspension.meta.duration} {creator.latest_suspension.meta.duration_unit}
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item", pl: 0, py: 0 }}>
              Limit:&nbsp;
              <Typography component={"span"} fontWeight={700}>
                {creator.latest_suspension.meta.max_request} per day
              </Typography>
            </ListItem>
          </List>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "0 !important", pt: "24px !important" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 2 }}>
          <Button fullWidth onClick={onClose} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            loadingPosition="start"
            variant="contained"
            color="success"
            size="small"
            onClick={handleLiftSuspension}
          >
            Lift Suspension
          </Button>
        </Box>
      </DialogActions>
    </Fragment>
  );
};

const SuspensionLiftedSuccessfully = ({ onClose }) => {
  return (
    <Fragment>
      <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            bgcolor: "#DEF2FC",
            color: "primary.main",
            borderRadius: 50,
            height: 150,
            width: 150,
          }}
        >
          <CheckCircle size={150} weight="thin" />
        </Box>
        <Typography textAlign={"center"} px={5} mb={1}>
          The suspension has been lifted from the creator’s account.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ padding: "0 !important", pt: "16px !important" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 2, justifyContent: "center" }}>
          <Button sx={{ width: " 50%" }} onClick={onClose} variant="contained">
            Close
          </Button>
        </Box>
      </DialogActions>
    </Fragment>
  );
};
